import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { AppShell, Topbar } from "@marekl/adminer/shell";
import { AdminerToaster, LoadingBar, OfflineBanner } from "@marekl/adminer/components";
import { useAuth } from "../../auth/useAuth";
import { useLogout } from "../../auth/queries";
import { Sidebar } from "../Sidebar";
import { InstanceTag } from "../InstanceTag";
import { QuickAdd } from "../QuickAdd";

/** Breadcrumb label for each top-level route. */
const ROUTE_LABELS: Record<string, string> = {
  "/audit-log": "Auditní log",
  "/settings": "Uživatelé a účet",
  "/companies": "Společnosti",
};

/** Legacy main titles of the sections inside a company. */
const COMPANY_LABELS: Record<string, string> = {
  invoices: "Vydaná faktura",
  "received-invoices": "Přijatá faktura",
  "credit-notes": "Dobropis",
  quotes: "Cenová nabídka",
  receipts: "Prodejky",
  documents: "Doklad",
  contacts: "Adresář",
  products: "Produkty",
  company: "Společnosti",
};

/**
 * Section-level default crumb: exact route first, then the longest prefix so
 * nested routes (e.g. "/invoices/123") resolve to their section label.
 * Pages needing a more specific trail call `useSetTopbarCrumbs` themselves.
 */
function routeLabel(pathname: string): string {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  const section = Object.keys(ROUTE_LABELS)
    .filter((path) => pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
  return section ? ROUTE_LABELS[section] : "";
}

export function AppLayout() {
  const { user } = useAuth();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const match = useMatch("/c/:companyId/*");

  if (!user) return null;

  // The legacy header: the section's title in capitals, no breadcrumbs.
  const title = match
    ? (COMPANY_LABELS[match.params["*"]?.split("/")[0] ?? ""] ?? "")
    : routeLabel(location.pathname);

  function handleLogout() {
    logout(undefined, { onSettled: () => navigate("/login", { replace: true }) });
  }

  return (
    <AppShell
      sidebar={<Sidebar user={user} onLogout={handleLogout} />}
      topbar={
        <Topbar
          title={title}
          right={
            <>
              <InstanceTag />
              <QuickAdd />
            </>
          }
        />
      }
      toaster={<AdminerToaster position="bottom-right" toastOptions={{ duration: 4000 }} />}
      navigationKey={location.pathname}
      // Legacy layout: the sidebar runs the full height with its company
      // header level with the top bar.
    >
      {/* The legacy red line along the top edge (tokens.css). */}
      <LoadingBar />
      <OfflineBanner position="top" />
      <Outlet />
    </AppShell>
  );
}
